import useRepo from '@/nebula/repo';
import { COUNT, CREATED_AT, RESULT, UID, VAR } from 'shared/api/query/constants';
import { children as aiChatChildren, chatList, pinnedChatList } from '@/api/query/nebula/ai-chat';
import {
  aiChat as aiChatConfig,
  aiChatMessage as aiChatMessageConfig,
} from 'shared/api/query/configs.json';
import { children as aiChatMessageChildren } from '@/api/query/nebula/ai-chat-message';
import { isPinned } from '@/lib/ai-chat-list';
import { ref } from 'vue';
import { reverseEdge } from 'shared/api/query/edges';

export default function useAiChatRepo() {
  const aiChatRepo = useRepo(aiChatConfig.model);
  const aiChatMessageRepo = useRepo(aiChatMessageConfig.model);

  const updateChat = (entity, options = {}) => aiChatRepo.updateSingle(entity, options);

  const deleteChat = (entity) => aiChatRepo.deleteMultiple([entity.uid]);

  const togglePin = (chat) => updateChat({
    uid: chat.uid,
    pinnedAt: isPinned(chat) ? null : new Date().toISOString(),
  });

  const queryPinnedChats = () => aiChatRepo.query(pinnedChatList());

  const createMessage = (entity) => aiChatMessageRepo.createSingle(entity);

  const retryPrompt = (uid) => aiChatMessageRepo.updateSingle({ uid }, { optimistic: false }, { retry: true });

  // the backend already deleted them; dropping them here does not wait for vortex to say so
  const discardMessages = (ids) => aiChatMessageRepo.deleteMultiple(ids, { commitToRemote: false });

  const getChatLoading = ref(false);
  const queryChat = (id) => {
    getChatLoading.value = true;
    return aiChatRepo.query([
      {
        alias: RESULT,
        func: { name: UID },
        uid: [id],
        model: aiChatConfig.model,
        children: aiChatChildren,
      },
      {
        alias: VAR,
        func: { name: UID },
        uid: [id],
        model: aiChatConfig.model,
        children: [
          {
            attr: reverseEdge(aiChatMessageConfig.edges.chat),
            model: aiChatMessageConfig.model,
            children: [{ attr: UID, var: 'messages' }],
          },
        ],
      },
      // TODO(AIC): paginate once chats can grow long.
      {
        alias: 'aiChatMessages',
        needsVar: [{ name: 'messages', typ: 1 }],
        func: { name: UID, needsVar: [{ name: 'messages', typ: 1 }] },
        model: aiChatMessageConfig.model,
        order: [{ attr: CREATED_AT, desc: false }],
        children: aiChatMessageChildren,
      },
    ])
      .then(() => aiChatRepo.selectSingle(id))
      .finally(() => {
        getChatLoading.value = false;
      });
  };

  const getListLoading = ref(false);
  const queryChatList = ({ pagination, searchTerm = '' }) => {
    const handleResponse = (response) => ({
      chats: aiChatRepo.selectMultiple(response[RESULT].map((e) => e.uid)),
      count: response[COUNT][0].count,
    });

    getListLoading.value = true;
    return aiChatRepo.query(chatList({
      pagination: { ...pagination, countAlias: COUNT },
      searchTerm,
    }))
      .then(handleResponse)
      .finally(() => {
        getListLoading.value = false;
      });
  };

  return {
    chats: aiChatRepo.entityList,
    selectChat: aiChatRepo.selectSingle,
    selectChats: aiChatRepo.selectMultiple,
    updateChat,
    deleteChat,
    togglePin,
    queryPinnedChats,
    queryChat,
    getChatLoading,
    queryChatList,
    getListLoading,

    messages: aiChatMessageRepo.entityList,
    selectMessage: aiChatMessageRepo.selectSingle,
    selectMessages: aiChatMessageRepo.selectMultiple,
    createMessage,
    retryPrompt,
    discardMessages,
  };
}
